import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { buildSetupShippingSummary } from '@/components/setup/experience/lib/setupShippingSummary';
import { useShippingRatesList, type ShippingRateItem } from '@/lib/api/shippingRates';

const SHIPPING_SUMMARY_QUERY = (storeId: string) => ({
  drizzQuery: {
    select: ['id', 'storeId', 'shippingRateName', 'icon', 'amount', 'countryCodes', 'isFree', 'createdAt'],
    from: 'shippingRates',
    where: [
      {
        table: 'shippingRates',
        column: 'storeId',
        operator: 'eq' as const,
        value: storeId,
      },
    ],
    orderBy: [
      {
        table: 'shippingRates',
        column: 'createdAt',
        direction: 'desc' as const,
      },
    ],
    limit: 100,
    offset: 0,
    joins: [],
  },
});

export function useSetupShippingSummary(
  storeId: string | undefined,
  currency: string,
  freeLabel: string,
  allCountriesLabel: string,
  enabled: boolean,
) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isFetching } = useShippingRatesList(SHIPPING_SUMMARY_QUERY(storeId ?? ''), {
    enabled: Boolean(storeId && enabled),
    refetchOnMount: 'always',
  });

  const items = (data?.items ?? []) as ShippingRateItem[];

  const formatMultiCountryLabel = useCallback(
    (count: number) => t('setup:experience.shipping.countryCount', { count }),
    [t],
  );

  const summary = useMemo(
    () =>
      buildSetupShippingSummary(
        items,
        currency,
        freeLabel,
        allCountriesLabel,
        formatMultiCountryLabel,
        i18n.language,
      ),
    [allCountriesLabel, currency, formatMultiCountryLabel, freeLabel, i18n.language, items],
  );

  return { summary, rates: items, isLoading: isLoading || isFetching };
}
